// Deploy: notification test fixes for withUserContext and withTenantTransaction mocks
import React, { Suspense, useEffect } from 'react';
import {
  Route,
  Navigate,
  Outlet,
  createBrowserRouter,
  createRoutesFromElements,
  useNavigate,
} from 'react-router-dom';
import { useAuthStore } from './store/auth.store';
import { useShareNotifications } from './hooks/useShareNotifications';
import { toast } from './utils/toast';
import { api } from './services/api';
import { secureStorage } from './services/secure-storage.service';
import { useDataChangeSocket } from './hooks/useDataChangeSocket';
import { useActiveIncidentSync } from './hooks/useActiveIncidentSync';
import { useZoneSyncListener } from './hooks/useZoneSyncListener';
import { useEmbeddedSessionBridge } from './hooks/useEmbeddedMode';
import { isEmbeddedMode } from './utils/embedded-console';
import { setGlobalNavigate } from './utils/global-navigate';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';
import Login from './pages/Login';
import Callback from './pages/Callback';

// Lazy load auth/onboarding pages
const AcceptInvitation = lazyWithRetry(() => import('./pages/AcceptInvitation'));
const SetupAccount = lazyWithRetry(() => import('./pages/SetupAccount'));
const MagicLinkLanding = lazyWithRetry(() => import('./pages/MagicLinkLanding'));
// Embedded web console sign-in from the native app (M2-CONSOLE).
const ConsoleHandoff = lazyWithRetry(() => import('./pages/ConsoleHandoff'));
const MagicLinkRequest = lazyWithRetry(() => import('./pages/MagicLinkRequest'));
const PasswordReset = lazyWithRetry(() => import('./pages/PasswordReset'));
const SsoCallback = lazyWithRetry(() => import('./pages/SsoCallback'));
const TrialActivate = lazyWithRetry(() => import('./pages/TrialActivate'));

// Lazy load core pages
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const OperationsDashboard = lazyWithRetry(() => import('./pages/Dashboard/OperationsDashboard'));
const LogisticsDashboard = lazyWithRetry(() => import('./pages/Dashboard/LogisticsDashboard'));
const SafetyDashboard = lazyWithRetry(() => import('./pages/Dashboard/SafetyDashboard'));
const Team = lazyWithRetry(() => import('./pages/Team'));
const Board = lazyWithRetry(() => import('./pages/Board'));
const AuditLogs = lazyWithRetry(() => import('./pages/AuditLogs'));
const VendorAccessLog = lazyWithRetry(() => import('./pages/VendorAccessLog'));

// Lazy load supply requests (named exports)
const SupplyRequestList = lazyWithRetry(() => import('./pages/SupplyRequests').then(m => ({ default: m.SupplyRequestList })));
const SupplyRequestDetailPage = lazyWithRetry(() => import('./pages/SupplyRequests').then(m => ({ default: m.SupplyRequestDetailPage })));
const SupplyRequestDashboard = lazyWithRetry(() => import('./pages/SupplyRequests').then(m => ({ default: m.SupplyRequestDashboard })));

// Lazy load tasks (named exports)
const TaskList = lazyWithRetry(() => import('./pages/Tasks').then(m => ({ default: m.TaskList })));
const TaskDetailPage = lazyWithRetry(() => import('./pages/Tasks').then(m => ({ default: m.TaskDetailPage })));

// Lazy load Forms (named exports)
const FormList = lazyWithRetry(() => import('./pages/Forms').then(m => ({ default: m.FormList })));
const FormDetail = lazyWithRetry(() => import('./pages/Forms').then(m => ({ default: m.FormDetail })));
const FormsAnalytics = lazyWithRetry(() => import('./pages/Forms/Analytics').then(m => ({ default: m.FormsAnalytics })));
const IAPAssembly = lazyWithRetry(() => import('./pages/Forms').then(m => ({ default: m.IAPAssembly })));

// Lazy load ICS 214 / Unit Logs (named exports)
const UnitLogList = lazyWithRetry(() => import('./pages/ICS214').then(m => ({ default: m.UnitLogList })));
const UnitLogDetail = lazyWithRetry(() => import('./pages/ICS214').then(m => ({ default: m.UnitLogDetail })));

// Lazy load incidents (named exports)
const IncidentList = lazyWithRetry(() => import('./pages/Incidents').then(m => ({ default: m.IncidentList })));
const IncidentDetail = lazyWithRetry(() => import('./pages/Incidents').then(m => ({ default: m.IncidentDetail })));
const IncidentCreationWizard = lazyWithRetry(() => import('./pages/Incidents').then(m => ({ default: m.IncidentCreationWizard })));
const QuickActivationWizard = lazyWithRetry(() => import('./pages/Incidents').then(m => ({ default: m.QuickActivationWizard })));
const ShiftHandoff = lazyWithRetry(() => import('./pages/Incidents').then(m => ({ default: m.ShiftHandoff })));

// Lazy load infrastructure pages (named exports)
const RoadClosures = lazyWithRetry(() => import('./pages/Infrastructure').then(m => ({ default: m.RoadClosures })));
const UtilityOutages = lazyWithRetry(() => import('./pages/Infrastructure').then(m => ({ default: m.UtilityOutages })));
const RoadClosureDetailPage = lazyWithRetry(() => import('./pages/Infrastructure').then(m => ({ default: m.RoadClosureDetailPage })));
const UtilityOutageDetailPage = lazyWithRetry(() => import('./pages/Infrastructure').then(m => ({ default: m.UtilityOutageDetailPage })));
const CriticalInfrastructure = lazyWithRetry(() => import('./pages/Infrastructure').then(m => ({ default: m.CriticalInfrastructure })));
const CriticalInfrastructureDetail = lazyWithRetry(() => import('./pages/Infrastructure').then(m => ({ default: m.CriticalInfrastructureDetail })));

// Lazy load damage assessments (named exports)
const DamageAssessmentList = lazyWithRetry(() => import('./pages/DamageAssessments').then(m => ({ default: m.DamageAssessmentList })));
// W13-DEBRIS: FEMA Category A debris estimates.
const DebrisList = lazyWithRetry(() => import('./pages/Debris').then(m => ({ default: m.DebrisList })));
const DebrisFormPage = lazyWithRetry(() => import('./pages/Debris').then(m => ({ default: m.DebrisFormPage })));
const DebrisDetailPage = lazyWithRetry(() => import('./pages/Debris').then(m => ({ default: m.DebrisDetailPage })));
const DamageAssessmentDetailPage = lazyWithRetry(() => import('./pages/DamageAssessments').then(m => ({ default: m.DamageAssessmentDetailPage })));
const PublicSubmissions = lazyWithRetry(() => import('./pages/DamageAssessments').then(m => ({ default: m.PublicSubmissions })));

// Lazy load community reports (named exports)
const CommunityReportsList = lazyWithRetry(() => import('./pages/CommunityReports').then(m => ({ default: m.CommunityReportsList })));

// Lazy load hazmat (named exports)
const HazmatList = lazyWithRetry(() => import('./pages/Hazmat').then(m => ({ default: m.HazmatList })));
const HazmatDetailPage = lazyWithRetry(() => import('./pages/Hazmat').then(m => ({ default: m.HazmatDetailPage })));

// Lazy load after action reviews (named exports)
const AARList = lazyWithRetry(() => import('./pages/AfterActionReviews').then(m => ({ default: m.AARList })));
const AARDetail = lazyWithRetry(() => import('./pages/AfterActionReviews').then(m => ({ default: m.AARDetail })));
const AARForm = lazyWithRetry(() => import('./pages/AfterActionReviews').then(m => ({ default: m.AARForm })));
const AARResponseForm = lazyWithRetry(() => import('./pages/AfterActionReviews').then(m => ({ default: m.AARResponseForm })));

// Lazy load COOP pages (named exports)
const CoopPlanList = lazyWithRetry(() => import('./pages/Coop').then(m => ({ default: m.CoopPlanList })));
const CoopPlanDetail = lazyWithRetry(() => import('./pages/Coop').then(m => ({ default: m.CoopPlanDetail })));
const CoopPlanForm = lazyWithRetry(() => import('./pages/Coop').then(m => ({ default: m.CoopPlanForm })));
const CoopActivationDashboard = lazyWithRetry(() => import('./pages/Coop').then(m => ({ default: m.CoopActivationDashboard })));
const BusinessContinuityDashboard = lazyWithRetry(() => import('./pages/BusinessContinuity').then(m => ({ default: m.BusinessContinuityDashboard })));
const SectorDetail = lazyWithRetry(() => import('./pages/BusinessContinuity').then(m => ({ default: m.SectorDetail })));

// Lazy load automation pages
const AutomationList = lazyWithRetry(() => import('./pages/Automations/AutomationList'));
const AutomationBuilder = lazyWithRetry(() => import('./pages/Automations/AutomationBuilder'));
const AutomationRunHistory = lazyWithRetry(() => import('./pages/Automations/AutomationRunHistory'));
const AutomationRunDetail = lazyWithRetry(() => import('./pages/Automations/AutomationRunDetail'));

// Lazy load map
const UnifiedMap = lazyWithRetry(() => import('./pages/Map').then(m => ({ default: m.UnifiedMap })));

// Lazy load shelters (named exports)
const ShelterList = lazyWithRetry(() => import('./pages/Shelters').then(m => ({ default: m.ShelterList })));
const ShelterDetailPage = lazyWithRetry(() => import('./pages/Shelters').then(m => ({ default: m.ShelterDetailPage })));

// Lazy load facilities (named exports)
const FacilitiesList = lazyWithRetry(() => import('./pages/Facilities').then(m => ({ default: m.FacilitiesList })));

// Lazy load PODs (named exports)
const PodsPage = lazyWithRetry(() => import('./pages/Pods').then(m => ({ default: m.PodsPage })));
const PodDetailPage = lazyWithRetry(() => import('./pages/Pods').then(m => ({ default: m.PodDetailPage })));

// Lazy load weather
const WeatherDashboard = lazyWithRetry(() => import('./pages/Weather').then(m => ({ default: m.WeatherDashboard })));
const FloodForecastHistory = lazyWithRetry(() => import('./pages/Weather/FloodForecastHistory'));

// Lazy load flood operations
const FloodOperationsPage = lazyWithRetry(() => import('./pages/FloodOperations/FloodOperationsPage'));
const TropicalPage = lazyWithRetry(() => import('./pages/Tropical/TropicalPage'));

// Lazy load at-risk registry
const AtRiskRegistryList = lazyWithRetry(() => import('./pages/AtRiskRegistry/AtRiskRegistryList'));
const AtRiskRegistryDetail = lazyWithRetry(() => import('./pages/AtRiskRegistry/AtRiskRegistryDetail'));

// Lazy load credentials (named exports)
const CredentialList = lazyWithRetry(() => import('./pages/Credentials').then(m => ({ default: m.CredentialList })));
const CredentialReview = lazyWithRetry(() => import('./pages/Credentials').then(m => ({ default: m.CredentialReview })));
const CredentialDetail = lazyWithRetry(() => import('./pages/Credentials').then(m => ({ default: m.CredentialDetail })));

// Lazy load inventory (named exports)
const InventoryList = lazyWithRetry(() => import('./pages/Inventory').then(m => ({ default: m.InventoryList })));
const InventoryLedger = lazyWithRetry(() => import('./pages/Inventory').then(m => ({ default: m.InventoryLedger })));
const InventoryDetail = lazyWithRetry(() => import('./pages/Inventory').then(m => ({ default: m.InventoryDetail })));
const InventoryForm = lazyWithRetry(() => import('./pages/Inventory').then(m => ({ default: m.InventoryForm })));
const InventoryImport = lazyWithRetry(() => import('./pages/Inventory').then(m => ({ default: m.InventoryImport })));
const StorageLocations = lazyWithRetry(() => import('./pages/Inventory').then(m => ({ default: m.StorageLocations })));
const InventoryAnalytics = lazyWithRetry(() => import('./pages/Inventory').then(m => ({ default: m.InventoryAnalytics })));

// Lazy load NIMS resources (named exports)
const ResourceCatalog = lazyWithRetry(() => import('./pages/Resources').then(m => ({ default: m.ResourceCatalog })));
const CatalogTypeDetail = lazyWithRetry(() => import('./pages/Resources').then(m => ({ default: m.CatalogTypeDetail })));
const ResourcePool = lazyWithRetry(() => import('./pages/Resources').then(m => ({ default: m.ResourcePool })));
const ResourceOrderingBoard = lazyWithRetry(() => import('./pages/Resources').then(m => ({ default: m.ResourceOrderingBoard })));
const ResourceRequestDetail = lazyWithRetry(() => import('./pages/Resources').then(m => ({ default: m.ResourceRequestDetail })));

// Lazy load contacts (named exports)
const ContactList = lazyWithRetry(() => import('./pages/Contacts').then(m => ({ default: m.ContactList })));
const ContactDetail = lazyWithRetry(() => import('./pages/Contacts').then(m => ({ default: m.ContactDetail })));

// Lazy load geofences (named exports)
const GeofenceList = lazyWithRetry(() => import('./pages/Geofences').then(m => ({ default: m.GeofenceList })));
const GeofenceDetail = lazyWithRetry(() => import('./pages/Geofences').then(m => ({ default: m.GeofenceDetail })));

// Lazy load agencies (named exports)
const AgencyList = lazyWithRetry(() => import('./pages/Agencies').then(m => ({ default: m.AgencyList })));
const AgencyDetail = lazyWithRetry(() => import('./pages/Agencies').then(m => ({ default: m.AgencyDetail })));

// Lazy load notifications (named exports)
const NotificationList = lazyWithRetry(() => import('./pages/Notifications').then(m => ({ default: m.NotificationList })));
const NotificationCompose = lazyWithRetry(() => import('./pages/Notifications').then(m => ({ default: m.NotificationCompose })));
const NotificationDetail = lazyWithRetry(() => import('./pages/Notifications').then(m => ({ default: m.NotificationDetail })));
const MyNotifications = lazyWithRetry(() => import('./pages/Notifications').then(m => ({ default: m.MyNotifications })));

// Lazy load IPAWS/WEA alerts (named exports)
const IpawsAlertList = lazyWithRetry(() => import('./pages/IPAWS').then(m => ({ default: m.IpawsAlertList })));
const IpawsAlertCompose = lazyWithRetry(() => import('./pages/IPAWS').then(m => ({ default: m.IpawsAlertCompose })));
const IpawsAlertDetail = lazyWithRetry(() => import('./pages/IPAWS').then(m => ({ default: m.IpawsAlertDetail })));

// Lazy load admin pages (named exports)
const AdminLayout = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminDashboard })));
const AdminTenants = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminTenants })));
const AdminUsers = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminUsers })));
const AdminPartnerships = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminPartnerships })));
const AdminShares = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminShares })));
const AdminInterfaces = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminInterfaces })));
const AdminHealth = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminHealth })));
const AdminMauiRollout = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminMauiRollout })));
const AdminErrorReports = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminErrorReports })));
const AdminAuditLogs = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminAuditLogs })));
const AdminFeatureFlags = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminFeatureFlags })));
const AdminAnnouncements = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminAnnouncements })));
const AdminApiKeys = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminApiKeys })));
const AdminLoginActivity = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminLoginActivity })));
const AdminFeatureFlagMatrix = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminFeatureFlagMatrix })));
const TenantOnboardingWizard = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.TenantOnboardingWizard })));
const AdminSupportTickets = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminSupportTickets })));
const AdminSupportTicketDetail = lazyWithRetry(() => import('./pages/admin').then(m => ({ default: m.AdminSupportTicketDetail })));

// Lazy load settings pages (named exports)
const SettingsLayout = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.SettingsLayout })));
const OrganizationSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.OrganizationSettings })));
const OperationalSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.OperationalSettings })));
const BrandingSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.BrandingSettings })));
const TemplatesSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.TemplatesSettings })));
const TemplateEditor = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.TemplateEditor })));
const RolesSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.RolesSettings })));
const TeamSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.TeamSettings })));
const ProtocolSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.ProtocolSettings })));
const QuickActionSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.QuickActionSettings })));
const NotificationTemplatesSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.NotificationTemplatesSettings })));
const OfflineDataSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.OfflineDataSettings })));
const PortalManagement = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.PortalManagement })));
const TaskTypeSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.TaskTypeSettings })));
const IntegrationsSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.IntegrationsSettings })));
const PdaZoneSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.PdaZoneSettings })));
const AppearanceSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.AppearanceSettings })));
const PositionSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.PositionSettings })));
const WeatherSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.WeatherSettings })));
const InventorySettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.InventorySettings })));
const InfrastructureTypesRoute = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.InfrastructureTypesRoute })));
const CredentialSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.CredentialSettingsTab })));
const ResourceTypeManager = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.ResourceTypeManager })));
const DataExportSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.DataExportSettings })));
const LegalHoldsSettings = lazyWithRetry(() => import('./pages/Settings/LegalHolds/LegalHoldsSettings'));
const ScimSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.ScimSettings })));
const DeviceSettings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.DeviceSettings })));


// Lazy load public portal pages (named exports)
const PublicPortal = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.PublicPortal })));
const DashboardTab = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.DashboardTab })));
const PostsTab = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.PostsTab })));
const RoadClosuresTab = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.RoadClosuresTab })));
const SheltersTab = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.SheltersTab })));
const BusinessesTab = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.BusinessesTab })));
const MapTab = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.MapTab })));
const AlertRegistration = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.AlertRegistration })));
const PublicDamageAssessment = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.PublicDamageAssessment })));
const BusinessStatusUpdate = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.BusinessStatusUpdate })));
const CommunityReportForm = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.CommunityReportForm })));
const CommunityReportsTab = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.CommunityReportsTab })));
const VerifyEmail = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.VerifyEmail })));
const Unsubscribe = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.Unsubscribe })));
const PublicLogin = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.PublicLogin })));
const PublicRegister = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.PublicRegister })));
const MagicLinkVerify = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.MagicLinkVerify })));
const PublicProfile = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.PublicProfile })));
const MySubmissions = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.MySubmissions })));
const MyBusinesses = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.MyBusinesses })));
const PortalPageView = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.PortalPageView })));
const ContactsPage = lazyWithRetry(() => import('./pages/public').then(m => ({ default: m.ContactsPage })));

// Lazy load dashboard builder pages (named exports)
const DashboardList = lazyWithRetry(() => import('./pages/Dashboards').then(m => ({ default: m.DashboardList })));
const DashboardBuilder = lazyWithRetry(() => import('./pages/Dashboards').then(m => ({ default: m.DashboardBuilder })));
const DashboardViewer = lazyWithRetry(() => import('./pages/Dashboards').then(m => ({ default: m.DashboardViewer })));

// Lazy load report pages
const ReportList = lazyWithRetry(() => import('./pages/Reports/ReportList'));
const ReportBuilder = lazyWithRetry(() => import('./pages/Reports/ReportBuilder'));
const ReportViewer = lazyWithRetry(() => import('./pages/Reports/ReportViewer'));
const SharedReportView = lazyWithRetry(() => import('./pages/Reports/SharedReportView'));

// Lazy load businesses (named export)
const BusinessList = lazyWithRetry(() => import('./pages/Businesses').then(m => ({ default: m.BusinessList })));

// Lazy load account settings (named export)
const AccountSettings = lazyWithRetry(() => import('./pages/Account').then(m => ({ default: m.AccountSettings })));
const MfaSetup = lazyWithRetry(() => import('./pages/MfaSetup'));

// Lazy load 404 page
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFound'));

// Lazy load help center (named exports)
const HelpCenter = lazyWithRetry(() => import('./pages/HelpCenter').then(m => ({ default: m.HelpCenter })));
const HelpCenterLayout = lazyWithRetry(() => import('./pages/HelpCenter').then(m => ({ default: m.HelpCenterLayout })));
const ArticlePage = lazyWithRetry(() => import('./pages/HelpCenter').then(m => ({ default: m.ArticlePage })));

// Lazy load support tickets (named exports)
const SupportTicketList = lazyWithRetry(() => import('./pages/Support').then(m => ({ default: m.SupportTicketList })));
const SupportTicketDetail = lazyWithRetry(() => import('./pages/Support').then(m => ({ default: m.SupportTicketDetail })));

// Lazy load public shared orthophoto viewer
const SharedOrthophotoViewer = lazyWithRetry(() => import('./pages/public/SharedOrthophotoViewer'));
// Lazy load 3D point cloud viewer
const PointCloudViewerPage = lazyWithRetry(() => import('./pages/SUAS/PointCloudViewerPage'));

// Lazy load SUAS pages to reduce main bundle size (~500KB savings)
const StreamViewer = lazyWithRetry(() => import('./pages/StreamViewer/StreamViewer'));
const CommandCenter = lazyWithRetry(() => import('./pages/SUAS/CommandCenter/CommandCenter'));
const SuasAnalytics = lazyWithRetry(() => import('./pages/SUAS/Analytics'));
const ComplianceDashboard = lazyWithRetry(() => import('./pages/SUAS/ComplianceDashboard'));
const FlightLogs = lazyWithRetry(() => import('./pages/SUAS/FlightLogs'));
const DroneList = lazyWithRetry(() => import('./pages/SUAS/Drones/DroneList'));
const DroneDetailPage = lazyWithRetry(() => import('./pages/SUAS/Drones/DroneDetailPage'));
const DroneFormPage = lazyWithRetry(() => import('./pages/SUAS/Drones/DroneFormPage'));
const OperatorList = lazyWithRetry(() => import('./pages/SUAS/Operators/OperatorList'));
const OperatorDetailPage = lazyWithRetry(() => import('./pages/SUAS/Operators/OperatorDetailPage'));
const OperatorFormPage = lazyWithRetry(() => import('./pages/SUAS/Operators/OperatorFormPage'));
const MissionList = lazyWithRetry(() => import('./pages/SUAS/Missions/MissionList'));
const MissionDetail = lazyWithRetry(() => import('./pages/SUAS/Missions/MissionDetail'));
const MissionPlanner = lazyWithRetry(() => import('./pages/SUAS/Missions/MissionPlanner'));
const MissionMonitor = lazyWithRetry(() => import('./pages/SUAS/Missions/MissionMonitor'));
// FleetDashboard, RealTimeFleetMap, LivestreamDashboard, MultiDroneOperationsDashboard
// are now consumed exclusively by CommandCenter tab modules and no longer imported here.
const SearchPatternCoordinator = lazyWithRetry(() => import('./pages/SUAS/Fleet/SearchPatternCoordinator'));
const DeviceManagement = lazyWithRetry(() => import('./pages/SUAS/Fleet/DeviceManagement'));
const ThermalInterface = lazyWithRetry(() => import('./pages/SUAS/Thermal/ThermalInterface'));
const AirspaceAuthorizations = lazyWithRetry(() => import('./pages/SUAS/Compliance/AirspaceAuthorizations'));
const MaintenanceLogs = lazyWithRetry(() => import('./pages/SUAS/Compliance/MaintenanceLogs'));
const PilotPerformance = lazyWithRetry(() => import('./pages/SUAS/Pilots/PilotPerformance'));
const SnapshotAnalysis = lazyWithRetry(() => import('./pages/SUAS/Analysis/SnapshotAnalysis'));
// Lazy load Wildfire platform pages (with retry for chunk-loading resilience)
const FirePerimeters = lazyWithRetry(() => import('./pages/Wildfire/FirePerimeters'));
const FirePerimeterDetail = lazyWithRetry(() => import('./pages/Wildfire/FirePerimeterDetail'));
const ManualPerimeterForm = lazyWithRetry(() => import('./pages/Wildfire/ManualPerimeterForm'));
const PersonnelTracking = lazyWithRetry(() => import('./pages/PersonnelTracking/PersonnelTracking'));
const AccountabilityPage = lazyWithRetry(() => import('./pages/Safety/AccountabilityPage'));
const OncallReachabilityPage = lazyWithRetry(() => import('./pages/Safety/OncallReachabilityPage'));
const RejectionQueuePage = lazyWithRetry(() => import('./pages/SyncRejections/RejectionQueuePage'));
const BridgeDrainDashboardPage = lazyWithRetry(() => import('./pages/BridgeDrain/BridgeDrainDashboardPage'));
const DeploymentsPage = lazyWithRetry(() => import('./pages/Deployments/DeploymentsPage'));
const EvacuationZones = lazyWithRetry(() => import('./pages/Evacuation/EvacuationZones'));
const EvacuationZoneDetail = lazyWithRetry(() => import('./pages/Evacuation/EvacuationZoneDetail'));
const CreateEvacuationZone = lazyWithRetry(() => import('./pages/Evacuation/CreateEvacuationZone'));
const FireBehaviorDashboard = lazyWithRetry(() => import('./pages/FireBehavior/FireBehaviorDashboard'));
const SmokeTrackingDashboard = lazyWithRetry(() => import('./pages/SmokeTracking/SmokeTrackingDashboard'));
const OnboardingWizard = lazyWithRetry(() => import('./pages/Onboarding/OnboardingWizard'));
const FileLibraryPage = lazyWithRetry(() => import('./pages/FileLibrary/FileLibraryPage'));
// Lazy load FEMA Reimbursement (named exports)
const FEMADashboard = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.FEMADashboard })));
const ProjectWorksheetList = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.ProjectWorksheetList })));
const ProjectWorksheetDetail = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.ProjectWorksheetDetail })));
const IATracker = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.IATracker })));
const FEMAExportPage = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.FEMAExportPage })));
const RateManagement = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.RateManagement })));
const AppealList = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.AppealList })));
const AppealDetail = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.AppealDetail })));
const CloseoutManager = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.CloseoutManager })));

// Lazy load Compensation/Claims pages (named exports)
const ClaimsList = lazyWithRetry(() => import('./pages/CompensationClaims').then(m => ({ default: m.ClaimsList })));
const ClaimDetailPage = lazyWithRetry(() => import('./pages/CompensationClaims').then(m => ({ default: m.ClaimDetailPage })));

// Lazy load PIO (Public Information Officer) pages
const PIODashboard = lazyWithRetry(() => import('./pages/PIO').then(m => ({ default: m.PIODashboard })));
const PIOStatementList = lazyWithRetry(() => import('./pages/PIO').then(m => ({ default: m.StatementList })));
const PIOStatementForm = lazyWithRetry(() => import('./pages/PIO').then(m => ({ default: m.StatementForm })));
const PIOStatementDetail = lazyWithRetry(() => import('./pages/PIO').then(m => ({ default: m.StatementDetail })));
const PIOMediaContacts = lazyWithRetry(() => import('./pages/PIO').then(m => ({ default: m.MediaContacts })));
const AuditPackages = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.AuditPackages })));
const CostAggregation = lazyWithRetry(() => import('./pages/FEMAReimbursement').then(m => ({ default: m.CostAggregation })));
const IncidentCostSummaryPage = lazyWithRetry(() => import('./pages/Finance').then(m => ({ default: m.IncidentCostSummary })));

// Lazy load Time Unit (ICS TNSP — timekeeping under Finance/Admin Section)
const TimesheetList = lazyWithRetry(() => import('./pages/TimeUnit').then(m => ({ default: m.TimesheetList })));
const TimesheetSummary = lazyWithRetry(() => import('./pages/TimeUnit').then(m => ({ default: m.TimesheetSummary })));

// Lazy load Equipment Unit (ICS equipment-usage approval queue under Finance/Admin Section)
const EquipmentUsageList = lazyWithRetry(() => import('./pages/EquipmentUnit').then(m => ({ default: m.EquipmentUsageList })));
const EquipmentUsageSummary = lazyWithRetry(() => import('./pages/EquipmentUnit').then(m => ({ default: m.EquipmentUsageSummary })));

// Lazy load Procurement (named exports)
const ProcurementList = lazyWithRetry(() => import('./pages/Procurement').then(m => ({ default: m.ProcurementList })));
const ProcurementDetail = lazyWithRetry(() => import('./pages/Procurement').then(m => ({ default: m.ProcurementDetail })));

// Lazy load Incident Organization (unified org tree — replaces Divisions & Groups in nav)
const IncidentOrgPage = lazyWithRetry(() => import('./pages/IncidentOrg').then(m => ({ default: m.IncidentOrgPage })));

// Lazy load Mutual Aid (named exports)
const MutualAidDashboard = lazyWithRetry(() => import('./pages/MutualAid').then(m => ({ default: m.MutualAidDashboard })));
const MutualAidAgreementList = lazyWithRetry(() => import('./pages/MutualAid').then(m => ({ default: m.AgreementList })));
const MutualAidAgreementDetail = lazyWithRetry(() => import('./pages/MutualAid').then(m => ({ default: m.AgreementDetail })));
const MutualAidAgreementForm = lazyWithRetry(() => import('./pages/MutualAid').then(m => ({ default: m.AgreementForm })));
const MutualAidRequestList = lazyWithRetry(() => import('./pages/MutualAid').then(m => ({ default: m.RequestList })));
const MutualAidRequestDetail = lazyWithRetry(() => import('./pages/MutualAid').then(m => ({ default: m.RequestDetail })));
const MutualAidRequestForm = lazyWithRetry(() => import('./pages/MutualAid').then(m => ({ default: m.RequestForm })));
const MutualAidCostTracker = lazyWithRetry(() => import('./pages/MutualAid').then(m => ({ default: m.CostTracker })));

// Lazy load Lifelines (named exports)
const LifelineDashboard = lazyWithRetry(() => import('./pages/Lifelines').then(m => ({ default: m.LifelineDashboard })));
const LifelineDetail = lazyWithRetry(() => import('./pages/Lifelines').then(m => ({ default: m.LifelineDetail })));

// Lazy load Recovery Dashboard
const RecoveryDashboard = lazyWithRetry(() => import('./pages/RecoveryDashboard').then(m => ({ default: m.RecoveryDashboard })));

// Lazy load Exercises (Exercise Mode control panel — named exports)
const ExercisesList = lazyWithRetry(() => import('./pages/Exercises').then(m => ({ default: m.ExercisesList })));
const ExerciseDetail = lazyWithRetry(() => import('./pages/Exercises').then(m => ({ default: m.ExerciseDetail })));

// Lazy load DJI Connect
const DJIConnectPage = lazyWithRetry(() => import('./pages/DJIConnect'));

import RouteAnnouncer from '@/components/layout/RouteAnnouncer';
import { MainLayout } from './components/layout';
import { RequireIncident, OnboardingGuard } from './components/routing';
import { RequirePermission } from './components/auth/RequirePermission';
import { RequireAnyPermission } from './components/auth/RequireAnyPermission';
import ServiceWorkerUpdateBanner from './components/common/ServiceWorkerUpdateBanner';
import ToastContainer from './components/common/ToastContainer';
import PageLoader from './components/common/PageLoader';
import FieldLayout from './components/field/FieldLayout';

const CommandDashboard = lazyWithRetry(() => import('./pages/Dashboard/CommandDashboard'));
const ForbiddenPage = lazyWithRetry(() => import('./pages/Forbidden'));

// Lazy load call center pages (named exports)
const CallCenterLayout = lazyWithRetry(() => import('./pages/CallCenter').then(m => ({ default: m.CallCenterLayout })));
const CallCenterWorkspace = lazyWithRetry(() => import('./pages/CallCenter').then(m => ({ default: m.CallCenterWorkspace })));

// Lazy load field operations pages (named exports)
const FieldDamageAssessmentForm = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldDamageAssessmentForm })));
const FieldDamageAssessmentDetail = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldDamageAssessmentDetail })));
const FieldRoadClosureForm = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldRoadClosureForm })));
const FieldRoadClosureDetail = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldRoadClosureDetail })));
const FieldTaskDetail = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldTaskDetail })));
const FieldPdaZoneMap = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldPdaZoneMap })));
const FieldPdaZoneSetup = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldPdaZoneSetup })));
const FieldPdaZoneManager = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldPdaZoneManager })));
const FieldSupplyRequestDetail = lazyWithRetry(() => import('./pages/Field').then(m => ({ default: m.FieldSupplyRequestDetail })));
const FieldTaskCreate = lazyWithRetry(() => import('./pages/Field/FieldTaskCreate'));
const FieldMap = lazyWithRetry(() => import('./pages/Field/FieldMap'));
const FieldSupplyRequests = lazyWithRetry(() => import('./pages/Field/FieldSupplyRequests'));
const FieldSupplyRequestForm = lazyWithRetry(() => import('./pages/Field/FieldSupplyRequestForm'));
const FieldShelterCheckin = lazyWithRetry(() => import('./pages/Field/FieldShelterCheckin'));
const FieldForms = lazyWithRetry(() => import('./pages/Field/FieldForms'));

// Public portal protected route wrapper - redirects to login if not authenticated
function PublicProtectedRoute({ children }: { children: React.ReactNode }) {
  const pathParts = window.location.pathname.split('/');
  const publicIdx = pathParts.indexOf('public');
  const slug = publicIdx >= 0 ? pathParts[publicIdx + 1] : '';
  const token = slug ? secureStorage.getSync(`portal_token_${slug}`) : null;
  if (!token) {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    return <Navigate to={`/public/${slug}/login?returnUrl=${returnUrl}`} replace />;
  }
  return <>{children}</>;
}

function AppRoot() {
  const navigate = useNavigate();
  useEffect(() => {
    setGlobalNavigate(navigate);
  }, [navigate]);

  const { isAuthenticated } = useAuthStore();
  const token = useAuthStore((s) => s.token);
  const permissions = useAuthStore((s) => s.permissions);
  const setPermissions = useAuthStore((s) => s.setPermissions);
  const isPlatformAdmin = useAuthStore((s) => s.user?.is_platform_admin);

  // PERMS-001: After a page reload (token rehydrated from secure storage)
  // the permissions array is persisted, but if a prior session never fetched
  // them (or if the user just logged in on a build that didn't populate
  // permissions yet), `hasPermission()` silently returns false. Backfill from
  // /auth/me whenever we're authenticated but the array is empty. Platform
  // admins bypass permission checks, so skip the fetch for them.
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    if (isPlatformAdmin) return;
    if (permissions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get<{ data: { permissions: { module: string; action: string }[] } }>(
          '/auth/me',
        );
        if (!cancelled) setPermissions(me.data?.permissions ?? []);
      } catch {
        // Tolerate failure: the api interceptor handles 401 (logout) and a
        // transient failure will be retried on the next render that satisfies
        // the same guard (e.g., next navigation).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, isPlatformAdmin, permissions.length, setPermissions]);

  // Announce mutual-aid incident shares sent to us by a partner tenant.
  // (Polled — see useShareNotifications; the socket event this used to wait for
  // was never emitted by the backend, so this toast never fired.)
  useShareNotifications((share) => {
    toast.action({
      title: 'New Incident Share',
      message: `${share.sender_tenant_name} shared "${share.incident_name}"`,
      actions: [
        { label: 'Accept', loadingLabel: 'Accepting...', variant: 'success',
          // merge_strategy is a scalar enum on the server ('create_new' |
          // 'merge_into_existing'); the per-datatype object this used to send
          // was rejected 400 VALIDATION_ERROR. 'create_new' is the only strategy
          // usable from a toast — 'merge_into_existing' needs a target incident.
          onClick: async () => { await api.post(`/incident-sharing/${share.id}/accept`, { merge_strategy: 'create_new' }); }
        },
        { label: 'Decline', loadingLabel: 'Declining...', variant: 'danger',
          onClick: async () => { await api.post(`/incident-sharing/${share.id}/decline`, { reason: 'Declined from notification' }); }
        },
      ],
      duration: 30000,
    });
  });

  // Global data-change listener: invalidates React Query caches when
  // any module broadcasts a CRUD event (incidents, shelters, etc.)
  useDataChangeSocket(isAuthenticated);

  // Embedded in the native app: tell its shell when the console session ends.
  useEmbeddedSessionBridge();

  // Rehydrate persisted active incident from server on reload
  // (only the incident ID is persisted; this fetches fresh data)
  useActiveIncidentSync();

  // Listen for zone sync push notifications and trigger offline downloads
  useZoneSyncListener();

  return (
    <ErrorBoundary>
      <ServiceWorkerUpdateBanner />
      <ToastContainer />
      <Outlet />
    </ErrorBoundary>
  );
}

function RequireAuth() {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) {
    // Embedded in the native app, the app signs the console in; a password
    // sign-in page inside the WebView would only mint a second, unbound session.
    return <Navigate to={isEmbeddedMode() ? '/handoff' : '/login'} replace />;
  }
  return <Outlet />;
}

/**
 * Names the standalone pages -- the ones outside the four layout shells.
 *
 * Pathless, so it changes no URL; it exists only to mount RouteAnnouncer for
 * routes that would otherwise inherit whatever title the last page left behind.
 */
function StandaloneRouteShell() {
  return (
    <>
      <RouteAnnouncer />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppRoot />}>
      {/*
        Everything below sits outside MainLayout, FieldLayout, AdminLayout and
        CallCenterLayout -- the four shells that mount RouteAnnouncer -- so
        until now none of it named itself. A password-reset tab, a trial
        activation and the onboarding wizard all read "Command Bridge", and
        navigating between them was silent to a screen reader.

        A pathless layout route is the way to add it once: nesting here does not
        change any URL, and it cannot double up with the four shells because
        these routes are siblings of them, not children.
      */}
      <Route element={<StandaloneRouteShell />}>
      {/* Public routes */}
      <Route path="/403" element={<Suspense fallback={<PageLoader />}><ForbiddenPage /></Suspense>} />
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Callback />} />
      <Route path="/invitations/:token" element={<Suspense fallback={<PageLoader />}><AcceptInvitation /></Suspense>} />
      <Route path="/setup-account/:token" element={<Suspense fallback={<PageLoader />}><SetupAccount /></Suspense>} />

      {/* Magic-link sign-in (Phase 2 — runs alongside Auth0 until cutover) */}
      <Route path="/auth/magic" element={<Suspense fallback={<PageLoader />}><MagicLinkLanding /></Suspense>} />
      {/* Native app -> embedded web console session handoff (code in the URL fragment) */}
      <Route path="/handoff" element={<Suspense fallback={<PageLoader />}><ConsoleHandoff /></Suspense>} />
      <Route path="/auth/magic-request" element={<Suspense fallback={<PageLoader />}><MagicLinkRequest /></Suspense>} />
      <Route path="/auth/reset-password" element={<Suspense fallback={<PageLoader />}><PasswordReset /></Suspense>} />

      {/* SAML SSO callback (Phase 4) — receives token from /api/sso/:tenant_slug/acs redirect */}
      <Route path="/auth/sso-callback" element={<Suspense fallback={<PageLoader />}><SsoCallback /></Suspense>} />

      {/* Trial activation — verification-email landing (provisions tenant + signs in) */}
      <Route path="/trial/activate" element={<Suspense fallback={<PageLoader />}><TrialActivate /></Suspense>} />

      {/* DJI Pilot 2 Connection Page (handles its own auth) */}
      <Route path="/dji-connect" element={<DJIConnectPage />} />

      {/* Public Citizen Portal (no auth required) */}
      <Route path="/public/:slug" element={<Suspense fallback={<PageLoader />}><PublicPortal /></Suspense>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><DashboardTab /></Suspense>} />
        <Route path="info" element={<Suspense fallback={<PageLoader />}><PostsTab /></Suspense>} />
        <Route path="roads" element={<Suspense fallback={<PageLoader />}><RoadClosuresTab /></Suspense>} />
        <Route path="shelters" element={<Suspense fallback={<PageLoader />}><SheltersTab /></Suspense>} />
        <Route path="businesses" element={<Suspense fallback={<PageLoader />}><BusinessesTab /></Suspense>} />
        <Route path="map" element={<Suspense fallback={<PageLoader />}><MapTab /></Suspense>} />
        <Route path="subscribe" element={<Suspense fallback={<PageLoader />}><AlertRegistration /></Suspense>} />
        <Route path="report-damage" element={<Suspense fallback={<PageLoader />}><PublicDamageAssessment /></Suspense>} />
        <Route path="submit-report" element={<Suspense fallback={<PageLoader />}><CommunityReportForm /></Suspense>} />
        <Route path="community-reports" element={<Suspense fallback={<PageLoader />}><CommunityReportsTab /></Suspense>} />
        <Route path="business-status" element={<Suspense fallback={<PageLoader />}><BusinessStatusUpdate /></Suspense>} />
        <Route path="contacts" element={<Suspense fallback={<PageLoader />}><ContactsPage /></Suspense>} />
        <Route path="page/:pageSlug" element={<Suspense fallback={<PageLoader />}><PortalPageView /></Suspense>} />
        {/* Public user auth routes */}
        <Route path="login" element={<Suspense fallback={<PageLoader />}><PublicLogin /></Suspense>} />
        <Route path="register" element={<Suspense fallback={<PageLoader />}><PublicRegister /></Suspense>} />
        <Route path="magic-login" element={<Suspense fallback={<PageLoader />}><MagicLinkVerify /></Suspense>} />
        {/* Public user protected routes */}
        <Route path="profile" element={<Suspense fallback={<PageLoader />}><PublicProtectedRoute><PublicProfile /></PublicProtectedRoute></Suspense>} />
        <Route path="my-submissions" element={<Suspense fallback={<PageLoader />}><PublicProtectedRoute><MySubmissions /></PublicProtectedRoute></Suspense>} />
        <Route path="my-businesses" element={<Suspense fallback={<PageLoader />}><PublicProtectedRoute><MyBusinesses /></PublicProtectedRoute></Suspense>} />
      </Route>
      <Route path="/public/:slug/verify-email/:token" element={<Suspense fallback={<PageLoader />}><VerifyEmail /></Suspense>} />
      <Route path="/public/:slug/unsubscribe/:token" element={<Suspense fallback={<PageLoader />}><Unsubscribe /></Suspense>} />

      {/* Public Dashboard Display (no auth required, token-based) */}
      <Route path="/display/:token" element={<Suspense fallback={<PageLoader />}><DashboardViewer /></Suspense>} />

      {/* Public Report Display (no auth required, token-based) */}
      <Route path="/display/reports/:token" element={<Suspense fallback={<PageLoader />}><SharedReportView /></Suspense>} />

      {/* Public Stream Viewer (no auth required, token-based) */}
      <Route path="/stream/:token" element={<Suspense fallback={<PageLoader />}><StreamViewer /></Suspense>} />

      {/* Public Orthophoto Share Viewer (no auth required, token-based) */}
      <Route path="/orthophoto/share/:token" element={<Suspense fallback={<PageLoader />}><SharedOrthophotoViewer /></Suspense>} />

      </Route>

      {/* Protected routes with layout */}
      <Route element={<RequireAuth />}>
          {/* Onboarding wizard (full-page, no sidebar, no guard).
              Wrapped too: it is the first screen a new account ever sees, and it
              sits outside MainLayout. */}
          <Route element={<StandaloneRouteShell />}>
            <Route path="/onboarding" element={<Suspense fallback={<PageLoader />}><OnboardingWizard /></Suspense>} />
          </Route>

          {/* All other routes require onboarding to be completed */}
          <Route element={<OnboardingGuard />}>
          {/* Main app routes */}
          <Route element={<MainLayout />}>
            {/* Routes accessible without an incident */}
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/dashboard/operations" element={<Suspense fallback={<PageLoader />}><OperationsDashboard /></Suspense>} />
            <Route path="/dashboard/logistics" element={<Suspense fallback={<PageLoader />}><LogisticsDashboard /></Suspense>} />
            <Route path="/dashboard/safety" element={<Suspense fallback={<PageLoader />}><SafetyDashboard /></Suspense>} />
            <Route path="/dashboard/command" element={<RequirePermission module="incidents" action="view"><Suspense fallback={<PageLoader />}><CommandDashboard /></Suspense></RequirePermission>} />
            <Route path="/map" element={<UnifiedMap />} />
            <Route path="/incidents" element={<IncidentList />} />
            <Route path="/incidents/new" element={<IncidentCreationWizard />} />
            <Route path="/incidents/quick-activate" element={<QuickActivationWizard />} />
            <Route path="/incidents/:id" element={<IncidentDetail />} />
            <Route path="/incidents/:id/shift-handoff" element={<ShiftHandoff />} />
            <Route path="/shelters" element={<ShelterList />} />
            <Route path="/shelters/:id" element={<ShelterDetailPage />} />
            <Route path="/facilities" element={<RequirePermission module="facilities" action="view"><Suspense fallback={<PageLoader />}><FacilitiesList /></Suspense></RequirePermission>} />
            <Route path="/pod-planning" element={<Navigate to="/pods?tab=templates" replace />} />
            {/* Permission module MUST stay hyphenated ('at-risk-registry'): that is the literal
                value seeded into permissions.module by migration 149 and used by every backend
                requirePermission() call and by Sidebar.tsx's nav gate. hasPermission() compares
                strings exactly, so an underscored variant here matches nothing and silently
                redirects every non-platform-admin (including the Admin role migration 149 grants
                all five at-risk-registry permissions to) to /403 — the sidebar link stays visible
                but the vulnerable-persons registry becomes unreachable mid-evacuation. Platform
                admins bypass hasPermission entirely, so this does not show up in admin smoke tests. */}
            <Route path="/at-risk-registry" element={<RequirePermission module="at-risk-registry" action="view"><AtRiskRegistryList /></RequirePermission>} />
            <Route path="/at-risk-registry/:id" element={<RequirePermission module="at-risk-registry" action="view"><AtRiskRegistryDetail /></RequirePermission>} />
            <Route path="/credentials" element={<RequirePermission module="credentials" action="view"><CredentialList /></RequirePermission>} />
            <Route path="/credentials/review" element={<RequirePermission module="credentials" action="view"><CredentialReview /></RequirePermission>} />
            <Route path="/credentials/:id" element={<RequirePermission module="credentials" action="view"><CredentialDetail /></RequirePermission>} />
            <Route path="/inventory" element={<InventoryList />} />
            <Route path="/inventory/new" element={<InventoryForm />} />
            <Route path="/inventory/import" element={<InventoryImport />} />
            <Route path="/inventory/locations" element={<StorageLocations />} />
            <Route path="/inventory/analytics" element={<InventoryAnalytics />} />
            <Route path="/inventory/ledger" element={<InventoryLedger />} />
            <Route path="/inventory/:consumableId/edit" element={<InventoryForm />} />
            <Route path="/inventory/:consumableId" element={<InventoryDetail />} />
            <Route path="/resources" element={<Navigate to="/resources/catalog" replace />} />
            <Route path="/resources/catalog" element={<RequirePermission module="resources" action="view"><ResourceCatalog /></RequirePermission>} />
            <Route path="/resources/catalog/:typeId" element={<RequirePermission module="resources" action="view"><CatalogTypeDetail /></RequirePermission>} />
            <Route path="/resources/pool" element={<RequirePermission module="resources" action="view"><ResourcePool /></RequirePermission>} />
            <Route path="/resources/requests" element={<RequirePermission module="resources" action="view"><ResourceOrderingBoard /></RequirePermission>} />
            <Route path="/resources/requests/:requestId" element={<RequirePermission module="resources" action="view"><ResourceRequestDetail /></RequirePermission>} />
            <Route path="/geofences" element={<GeofenceList />} />
            <Route path="/geofences/:id" element={<GeofenceDetail />} />
            <Route path="/agencies" element={<AgencyList />} />
            <Route path="/agencies/:id" element={<AgencyDetail />} />
            <Route path="/team" element={<Team />} />
            <Route path="/audit-logs" element={<RequirePermission module="audit" action="view"><AuditLogs /></RequirePermission>} />
            <Route path="/audit-logs/vendor-access" element={<RequirePermission module="audit" action="view"><VendorAccessLog /></RequirePermission>} />
            <Route path="/contacts" element={<ContactList />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/my-notifications" element={<MyNotifications />} />
            <Route path="/notifications" element={<NotificationList />} />
            <Route path="/notifications/compose" element={<NotificationCompose />} />
            <Route path="/notifications/:id" element={<NotificationDetail />} />
            <Route path="/ipaws" element={<Navigate to="/ipaws/alerts" replace />} />
            <Route path="/ipaws/alerts" element={<IpawsAlertList />} />
            <Route path="/ipaws/alerts/compose" element={<IpawsAlertCompose />} />
            {/* Must be registered BEFORE (or alongside) `:id` — React Router v6
                `:id` is single-segment, so without this route the "Edit" button
                on a draft WEA fell through to the 404 catch-all and a public
                alert could not be corrected. */}
            <Route path="/ipaws/alerts/:id/edit" element={<IpawsAlertCompose />} />
            <Route path="/ipaws/alerts/:id" element={<IpawsAlertDetail />} />
            <Route path="/dashboards" element={<DashboardList />} />
            <Route path="/dashboards/new" element={<DashboardBuilder />} />
            <Route path="/dashboards/:id" element={<DashboardBuilder />} />
            <Route path="/reports" element={<ReportList />} />
            <Route path="/reports/new" element={<ReportBuilder />} />
            <Route path="/reports/:id" element={<ReportBuilder />} />
            <Route path="/reports/:id/view" element={<ReportViewer />} />
            <Route path="/businesses" element={<BusinessList />} />

            {/* PIO (Public Information Officer) routes */}
            <Route path="/pio" element={<Suspense fallback={<PageLoader />}><PIODashboard /></Suspense>} />
            <Route path="/pio/statements" element={<Suspense fallback={<PageLoader />}><PIOStatementList /></Suspense>} />
            <Route path="/pio/statements/new" element={<Suspense fallback={<PageLoader />}><PIOStatementForm /></Suspense>} />
            <Route path="/pio/statements/:id" element={<Suspense fallback={<PageLoader />}><PIOStatementDetail /></Suspense>} />
            <Route path="/pio/statements/:id/edit" element={<Suspense fallback={<PageLoader />}><PIOStatementForm /></Suspense>} />
            <Route path="/pio/media-contacts" element={<Suspense fallback={<PageLoader />}><PIOMediaContacts /></Suspense>} />

            {/* Time Unit (ICS TNSP — Finance/Admin Section timekeeping) */}
            <Route path="/time-unit" element={<Suspense fallback={<PageLoader />}><TimesheetList /></Suspense>} />
            <Route path="/time-unit/incident/:id/summary" element={<Suspense fallback={<PageLoader />}><TimesheetSummary /></Suspense>} />

            {/* Equipment Unit (equipment-usage approval queue) */}
            <Route path="/equipment-usage" element={<Suspense fallback={<PageLoader />}><EquipmentUsageList /></Suspense>} />
            <Route path="/equipment-usage/incident/:id/summary" element={<Suspense fallback={<PageLoader />}><EquipmentUsageSummary /></Suspense>} />

            <Route path="/files" element={<Suspense fallback={<PageLoader />}><FileLibraryPage /></Suspense>} />
            <Route path="/files/folder/:folderId" element={<Suspense fallback={<PageLoader />}><FileLibraryPage /></Suspense>} />
            <Route path="/account" element={<AccountSettings />} />
            <Route path="/account/mfa-setup" element={<Suspense fallback={<PageLoader />}><MfaSetup /></Suspense>} />

            {/* Help Center routes */}
            <Route path="/help" element={<HelpCenterLayout />}>
              <Route index element={<HelpCenter />} />
              <Route path=":category" element={<HelpCenter />} />
              <Route path=":category/:article" element={<ArticlePage />} />
            </Route>

            {/* Support tickets (incident-independent) */}
            <Route path="/support" element={<Suspense fallback={<PageLoader />}><SupportTicketList /></Suspense>} />
            <Route path="/support/:id" element={<Suspense fallback={<PageLoader />}><SupportTicketDetail /></Suspense>} />

            {/* COOP routes (incident-independent) */}
            <Route path="/coop" element={<Navigate to="/coop/plans" replace />} />
            <Route path="/coop/plans" element={<CoopPlanList />} />
            <Route path="/coop/plans/new" element={<CoopPlanForm />} />
            <Route path="/coop/plans/:id" element={<CoopPlanDetail />} />
            <Route path="/coop/plans/:id/edit" element={<CoopPlanForm />} />
            <Route path="/coop/activations/:id" element={<CoopActivationDashboard />} />

            {/* Business Continuity (incident-independent) */}
            <Route path="/business-continuity" element={<BusinessContinuityDashboard />} />
            <Route path="/business-continuity/sectors/:id" element={<SectorDetail />} />

            {/* Automations (incident-independent) */}
            <Route path="/automations" element={<AutomationList />} />
            <Route path="/automations/new" element={<AutomationBuilder />} />
            <Route path="/automations/runs/:runId" element={<AutomationRunDetail />} />
            <Route path="/automations/:id/edit" element={<AutomationBuilder />} />
            <Route path="/automations/:id/runs" element={<AutomationRunHistory />} />

            {/* sUAS / Drone Management (incident-independent) - Lazy loaded for performance */}
            <Route path="/suas" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><CommandCenter /></ErrorBoundary></Suspense>} />
            <Route path="/suas/analytics" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><SuasAnalytics /></ErrorBoundary></Suspense>} />
            <Route path="/suas/compliance" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><ComplianceDashboard /></ErrorBoundary></Suspense>} />
            <Route path="/suas/flight-logs" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><FlightLogs /></ErrorBoundary></Suspense>} />

            {/* Drones */}
            <Route path="/suas/drones" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><DroneList /></ErrorBoundary></Suspense>} />
            <Route path="/suas/drones/new" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><DroneFormPage /></ErrorBoundary></Suspense>} />
            <Route path="/suas/drones/:id" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><DroneDetailPage /></ErrorBoundary></Suspense>} />
            <Route path="/suas/drones/:id/edit" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><DroneFormPage /></ErrorBoundary></Suspense>} />

            {/* Operators */}
            <Route path="/suas/operators" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><OperatorList /></ErrorBoundary></Suspense>} />
            <Route path="/suas/operators/new" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><OperatorFormPage /></ErrorBoundary></Suspense>} />
            <Route path="/suas/operators/:id" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><OperatorDetailPage /></ErrorBoundary></Suspense>} />
            <Route path="/suas/operators/:id/edit" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><OperatorFormPage /></ErrorBoundary></Suspense>} />

            {/* Missions */}
            <Route path="/suas/missions" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><MissionList /></ErrorBoundary></Suspense>} />
            <Route path="/suas/missions/new" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><MissionPlanner /></ErrorBoundary></Suspense>} />
            <Route path="/suas/missions/:id" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><MissionDetail /></ErrorBoundary></Suspense>} />
            <Route path="/suas/missions/:id/edit" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><MissionPlanner /></ErrorBoundary></Suspense>} />
            <Route path="/suas/missions/:id/monitor" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><MissionMonitor /></ErrorBoundary></Suspense>} />

            {/* Fleet — three standalone dashboards consolidated into CommandCenter tabs.
                Legacy URLs redirect to the appropriate tab for bookmark compatibility. */}
            <Route path="/suas/fleet" element={<Navigate to="/suas" replace />} />
            <Route path="/suas/fleet/devices" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><DeviceManagement /></ErrorBoundary></Suspense>} />
            <Route path="/suas/fleet/map" element={<Navigate to="/suas?tab=map" replace />} />
            <Route path="/suas/fleet/livestream" element={<Navigate to="/suas?tab=feeds" replace />} />
            <Route path="/suas/fleet/search-patterns" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><SearchPatternCoordinator /></ErrorBoundary></Suspense>} />

            {/* Real-Time Operations Dashboard — consolidated into CommandCenter Telemetry tab */}
            <Route path="/suas/operations" element={<Navigate to="/suas?tab=telemetry" replace />} />

            {/* Thermal Imaging */}
            <Route path="/suas/thermal/:missionId" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><ThermalInterface /></ErrorBoundary></Suspense>} />

            {/* Compliance */}
            <Route path="/suas/compliance/authorizations" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><AirspaceAuthorizations /></ErrorBoundary></Suspense>} />
            <Route path="/suas/compliance/maintenance" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><MaintenanceLogs /></ErrorBoundary></Suspense>} />

            {/* Pilots */}
            <Route path="/suas/pilots" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><PilotPerformance /></ErrorBoundary></Suspense>} />
            <Route path="/suas/pilots/:id" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><PilotPerformance /></ErrorBoundary></Suspense>} />

            {/* Analysis */}
            <Route path="/suas/analysis" element={<Navigate to="/suas/analysis/snapshots" replace />} />
            <Route path="/suas/analysis/snapshots" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><SnapshotAnalysis /></ErrorBoundary></Suspense>} />

            {/* 3D Point Cloud Viewer */}
            <Route path="/suas/pointcloud/:taskId" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><PointCloudViewerPage /></ErrorBoundary></Suspense>} />

            {/* Wildfire Platform (incident-independent) - Lazy loaded */}
            <Route path="/wildfire" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><FirePerimeters /></ErrorBoundary></Suspense>} />
            <Route path="/wildfire/new" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><ManualPerimeterForm /></ErrorBoundary></Suspense>} />
            <Route path="/wildfire/:id" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><FirePerimeterDetail /></ErrorBoundary></Suspense>} />
            <Route path="/personnel-tracking" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><PersonnelTracking /></ErrorBoundary></Suspense>} />
            <Route path="/safety/accountability" element={<RequirePermission module="personnel_tracking" action="view"><Suspense fallback={<PageLoader />}><ErrorBoundary><AccountabilityPage /></ErrorBoundary></Suspense></RequirePermission>} />
            <Route path="/safety/on-call" element={<RequireAnyPermission permissions={[['settings', 'manage'], ['personnel_tracking', 'respond']]}><Suspense fallback={<PageLoader />}><ErrorBoundary><OncallReachabilityPage /></ErrorBoundary></Suspense></RequireAnyPermission>} />
            <Route path="/field-changes/refused" element={<RequirePermission module="sync_rejections" action="view"><Suspense fallback={<PageLoader />}><ErrorBoundary><RejectionQueuePage /></ErrorBoundary></Suspense></RequirePermission>} />
            <Route path="/field-changes/app-upgrade" element={<RequirePermission module="sync_rejections" action="view"><Suspense fallback={<PageLoader />}><ErrorBoundary><BridgeDrainDashboardPage /></ErrorBoundary></Suspense></RequirePermission>} />
            <Route path="/deployments" element={<RequirePermission module="team" action="view"><Suspense fallback={<PageLoader />}><ErrorBoundary><DeploymentsPage /></ErrorBoundary></Suspense></RequirePermission>} />
            <Route path="/evacuation" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><EvacuationZones /></ErrorBoundary></Suspense>} />
            <Route path="/evacuation/new" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><CreateEvacuationZone /></ErrorBoundary></Suspense>} />
            <Route path="/evacuation/:id" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><EvacuationZoneDetail /></ErrorBoundary></Suspense>} />
            <Route path="/fire-resources" element={<Navigate to="/inventory?view=operational" replace />} />
            <Route path="/fire-behavior" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><FireBehaviorDashboard /></ErrorBoundary></Suspense>} />
            <Route path="/smoke-tracking" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><SmokeTrackingDashboard /></ErrorBoundary></Suspense>} />

            {/* Procurement (incident-independent) */}
            <Route path="/procurement" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><ProcurementList /></ErrorBoundary></Suspense>} />
            <Route path="/procurement/:id" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><ProcurementDetail /></ErrorBoundary></Suspense>} />

            {/* Divisions & Groups (ICS Operations Section hierarchy, incident-scoped) */}

            {/* Incident Organization (unified org tree, incident-scoped) */}
            <Route path="/incident-org" element={<RequirePermission module="incident_org" action="view"><IncidentOrgPage /></RequirePermission>} />

            {/* Legacy liaison routes — consolidated into the Mutual Aid hub */}
            <Route path="/liaison-dashboard" element={<Navigate to="/mutual-aid" replace />} />
            <Route path="/liaison" element={<Navigate to="/liaison/agreements" replace />} />
            <Route path="/liaison/agreements" element={<Navigate to="/mutual-aid/agreements" replace />} />

            {/* Mutual Aid (incident-independent) */}
            <Route path="/mutual-aid" element={<MutualAidDashboard />} />
            <Route path="/mutual-aid/agreements" element={<MutualAidAgreementList />} />
            <Route path="/mutual-aid/agreements/new" element={<MutualAidAgreementForm />} />
            <Route path="/mutual-aid/agreements/:id" element={<MutualAidAgreementDetail />} />
            <Route path="/mutual-aid/agreements/:id/edit" element={<MutualAidAgreementForm />} />
            <Route path="/mutual-aid/requests" element={<MutualAidRequestList />} />
            <Route path="/mutual-aid/requests/new" element={<MutualAidRequestForm />} />
            <Route path="/mutual-aid/requests/:id" element={<MutualAidRequestDetail />} />
            <Route path="/mutual-aid/requests/:id/edit" element={<MutualAidRequestForm />} />
            <Route path="/mutual-aid/costs" element={<MutualAidCostTracker />} />
            <Route path="/mutual-aid/partner" element={<Navigate to="/mutual-aid?tab=partner-requests" replace />} />

            {/* FEMA Rate Management (not incident-scoped) */}
            <Route path="/fema-reimbursement/rates" element={<RequirePermission module="fema_reimbursement" action="view"><RateManagement /></RequirePermission>} />

            {/* Weather */}
            <Route path="/weather" element={<WeatherDashboard />} />
            <Route path="/weather/flood-history/:stationId?" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><FloodForecastHistory /></ErrorBoundary></Suspense>} />

            {/* Flood Operations */}
            <Route path="/flood-operations" element={<Suspense fallback={<PageLoader />}><ErrorBoundary><FloodOperationsPage /></ErrorBoundary></Suspense>} />
            <Route path="/tropical" element={<RequirePermission module="map" action="view"><Suspense fallback={<PageLoader />}><ErrorBoundary><TropicalPage /></ErrorBoundary></Suspense></RequirePermission>} />

            {/* W13-DEBRIS: debris estimates. Not behind RequireIncident: the map's
                "Record here" passes ?incident= (and the point) in the URL. */}
            <Route path="/debris" element={<RequirePermission module="debris" action="view"><DebrisList /></RequirePermission>} />
            <Route path="/debris/new" element={<RequirePermission module="debris" action="create"><DebrisFormPage /></RequirePermission>} />
            <Route path="/debris/:id" element={<RequirePermission module="debris" action="view"><DebrisDetailPage /></RequirePermission>} />

            {/* Routes that require an engaged incident */}
            <Route element={<RequireIncident />}>
              <Route path="/tasks" element={<TaskList />} />
              <Route path="/tasks/:id" element={<TaskDetailPage />} />
              <Route path="/forms" element={<FormList />} />
              <Route path="/forms/analytics" element={<FormsAnalytics />} />
              <Route path="/forms/iap" element={<IAPAssembly />} />
              <Route path="/forms/:id" element={<FormDetail />} />
              <Route path="/ics214" element={<UnitLogList />} />
              <Route path="/ics214/:id" element={<UnitLogDetail />} />
              <Route path="/board" element={<Board />} />
              <Route path="/supply-requests" element={<SupplyRequestList />} />
              <Route path="/supply-requests/dashboard" element={<SupplyRequestDashboard />} />
              <Route path="/supply-requests/:id" element={<SupplyRequestDetailPage />} />
              <Route path="/pods" element={<PodsPage />} />
              <Route path="/pods/:id" element={<PodDetailPage />} />
              <Route path="/damage-assessments" element={<DamageAssessmentList />} />
              <Route path="/damage-assessments/public-submissions" element={<PublicSubmissions />} />
              <Route path="/damage-assessments/:id" element={<DamageAssessmentDetailPage />} />
              <Route path="/community-reports" element={<Navigate to="/community-reports/submissions" replace />} />
              <Route path="/community-reports/submissions" element={<CommunityReportsList />} />
              <Route path="/hazmat" element={<HazmatList />} />
              <Route path="/hazmat/:id" element={<HazmatDetailPage />} />
              <Route path="/claims" element={<RequirePermission module="comp_claims" action="view"><Suspense fallback={<PageLoader />}><ClaimsList /></Suspense></RequirePermission>} />
              <Route path="/claims/:id" element={<RequirePermission module="comp_claims" action="view"><Suspense fallback={<PageLoader />}><ClaimDetailPage /></Suspense></RequirePermission>} />
              <Route path="/after-action-reviews" element={<AARList />} />
              <Route path="/after-action-reviews/new" element={<AARForm />} />
              <Route path="/after-action-reviews/:id" element={<AARDetail />} />
              <Route path="/after-action-reviews/:id/edit" element={<AARForm />} />
              <Route path="/after-action-reviews/:id/respond" element={<AARResponseForm />} />
              <Route path="/fema-reimbursement" element={<RequirePermission module="fema_reimbursement" action="view"><FEMADashboard /></RequirePermission>} />
              <Route path="/fema-reimbursement/worksheets" element={<RequirePermission module="fema_reimbursement" action="view"><ProjectWorksheetList /></RequirePermission>} />
              <Route path="/fema-reimbursement/worksheets/:id" element={<RequirePermission module="fema_reimbursement" action="view"><ProjectWorksheetDetail /></RequirePermission>} />
              <Route path="/fema-reimbursement/worksheets/:id/closeout" element={<RequirePermission module="fema_reimbursement" action="view"><CloseoutManager /></RequirePermission>} />
              <Route path="/fema-reimbursement/ia-cases" element={<RequirePermission module="fema_reimbursement" action="view"><IATracker /></RequirePermission>} />
              <Route path="/fema-reimbursement/appeals" element={<RequirePermission module="fema_reimbursement" action="view"><AppealList /></RequirePermission>} />
              <Route path="/fema-reimbursement/appeals/:id" element={<RequirePermission module="fema_reimbursement" action="view"><AppealDetail /></RequirePermission>} />
              <Route path="/fema-reimbursement/audit-packages" element={<RequirePermission module="fema_reimbursement" action="view"><AuditPackages /></RequirePermission>} />
              <Route path="/fema-reimbursement/cost-aggregation" element={<RequirePermission module="fema_reimbursement" action="view"><CostAggregation /></RequirePermission>} />
              <Route path="/fema-reimbursement/export" element={<RequirePermission module="fema_reimbursement" action="view"><FEMAExportPage /></RequirePermission>} />
              <Route path="/finance/incidents/:incidentId" element={<RequirePermission module="cost" action="view"><IncidentCostSummaryPage /></RequirePermission>} />
              <Route path="/lifelines" element={<LifelineDashboard />} />
              <Route path="/lifelines/:lifelineId" element={<LifelineDetail />} />
              <Route path="/recovery" element={<RecoveryDashboard />} />
            </Route>

            {/*
              Exercises are incident-INDEPENDENT (see CLAUDE.md's module classification), and
              neither ExercisesList nor CreateExerciseModal reads an active incident — the list is a
              plain tenant-scoped /exercises query. Behind RequireIncident the module was not just
              needlessly gated, it was gated backwards: an exercise IS an incident
              (is_exercise = true), so reaching the drill planner meant first engaging a REAL
              incident — the one state in which nobody should be authoring drills, and precisely
              the contamination the exercise quarantine exists to prevent.
            */}
            {/*
              F-030: Infrastructure is incident-INDEPENDENT — CLAUDE.md lists
              "Businesses/Infrastructure" as global within the tenant, the unified map treats
              critical_infrastructure / road_closures / utility_outages as always-shown
              incident-independent layers, and the records themselves are tenant-global (a
              brand-new incident immediately listed a dozen pre-existing ones belonging to
              nobody).

              Behind RequireIncident the map could render this data with no incident engaged
              while the pages that manage the same data were unreachable without one.
            */}
            <Route path="/infrastructure" element={<Navigate to="/infrastructure/critical" replace />} />
            <Route path="/infrastructure/road-closures" element={<RoadClosures />} />
            <Route path="/infrastructure/road-closures/:id" element={<RoadClosureDetailPage />} />
            <Route path="/infrastructure/utility-outages" element={<UtilityOutages />} />
            <Route path="/infrastructure/utility-outages/:id" element={<UtilityOutageDetailPage />} />
            <Route path="/infrastructure/critical" element={<CriticalInfrastructure />} />
            <Route path="/infrastructure/critical/:id" element={<CriticalInfrastructureDetail />} />

            <Route path="/exercises" element={<RequirePermission module="exercises" action="view"><ExercisesList /></RequirePermission>} />
            <Route path="/exercises/:exerciseId" element={<RequirePermission module="exercises" action="view"><ExerciseDetail /></RequirePermission>} />

            {/* Settings routes (nested layout) */}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/settings/organization" replace />} />
              <Route path="organization" element={<OrganizationSettings />} />
              <Route path="operational" element={<OperationalSettings />} />
              <Route path="appearance" element={<AppearanceSettings />} />
              <Route path="branding" element={<BrandingSettings />} />
              <Route path="team" element={<RequirePermission module="settings" action="manage"><TeamSettings /></RequirePermission>} />
              <Route path="roles" element={<RequirePermission module="settings" action="manage"><RolesSettings /></RequirePermission>} />
              <Route path="devices" element={<DeviceSettings />} />
              <Route path="positions" element={<PositionSettings />} />
              <Route path="templates" element={<TemplatesSettings />} />
              <Route path="templates/:id" element={<TemplateEditor />} />
              <Route path="notification-templates" element={<NotificationTemplatesSettings />} />
              <Route path="task-types" element={<TaskTypeSettings />} />
              <Route path="protocols" element={<ProtocolSettings />} />
              <Route path="quick-actions" element={<QuickActionSettings />} />
              {/* Offline basemap downloads were removed (P0-20); old links land on Offline Data. */}
              <Route path="offline-maps" element={<Navigate to="/settings/offline-data" replace />} />
              <Route path="offline-data" element={<OfflineDataSettings />} />
              <Route path="portal" element={<PortalManagement />} />
              <Route path="eoc-displays" element={<DashboardList />} />
              <Route path="pda-zones" element={<PdaZoneSettings />} />
              <Route path="inventory" element={<InventorySettings />} />
              <Route path="credentials" element={<CredentialSettings />} />
              <Route path="infrastructure-types/*" element={<InfrastructureTypesRoute />} />
              <Route path="resource-types" element={<ResourceTypeManager />} />
              <Route path="integrations" element={<IntegrationsSettings />} />
              <Route path="weather" element={<WeatherSettings />} />
              <Route path="data-export" element={<DataExportSettings />} />
              <Route path="legal-holds" element={<RequirePermission module="legal_hold" action="view"><LegalHoldsSettings /></RequirePermission>} />
              <Route path="scim" element={<RequirePermission module="settings" action="manage"><ScimSettings /></RequirePermission>} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Catch-all 404 for authenticated users */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Call Center mode (separate layout) */}
          <Route element={<RequireIncident />}>
            <Route path="/call-center" element={<Suspense fallback={<PageLoader />}><CallCenterLayout /></Suspense>}>
              <Route index element={<Suspense fallback={<PageLoader />}><CallCenterWorkspace /></Suspense>} />
            </Route>
          </Route>

          {/* Field Operations mode (separate layout) */}
          {/* RequireIncident is handled inside FieldLayout to allow inline incident selection */}
          <Route path="/field" element={<FieldLayout />}>
              <Route index element={<Suspense fallback={<PageLoader />}><FieldMap /></Suspense>} />
              <Route path="damage/new" element={<Suspense fallback={<PageLoader />}><FieldDamageAssessmentForm /></Suspense>} />
              <Route path="damage/:id" element={<Suspense fallback={<PageLoader />}><FieldDamageAssessmentDetail /></Suspense>} />
              <Route path="roads/new" element={<Suspense fallback={<PageLoader />}><FieldRoadClosureForm /></Suspense>} />
              <Route path="roads/:id" element={<Suspense fallback={<PageLoader />}><FieldRoadClosureDetail /></Suspense>} />
              <Route path="zones/setup" element={<Suspense fallback={<PageLoader />}><FieldPdaZoneSetup /></Suspense>} />
              <Route path="zones/manage/:setId" element={<Suspense fallback={<PageLoader />}><FieldPdaZoneManager /></Suspense>} />
              <Route path="zones/:id/map" element={<Suspense fallback={<PageLoader />}><FieldPdaZoneMap /></Suspense>} />
              <Route path="tasks/new" element={<Suspense fallback={<PageLoader />}><FieldTaskCreate /></Suspense>} />
              <Route path="tasks/:id" element={<Suspense fallback={<PageLoader />}><FieldTaskDetail /></Suspense>} />
              <Route path="supplies" element={<Suspense fallback={<PageLoader />}><FieldSupplyRequests /></Suspense>} />
              <Route path="supplies/new" element={<Suspense fallback={<PageLoader />}><FieldSupplyRequestForm /></Suspense>} />
              <Route path="supplies/:id" element={<Suspense fallback={<PageLoader />}><FieldSupplyRequestDetail /></Suspense>} />
              <Route path="shelter-checkin" element={<Suspense fallback={<PageLoader />}><FieldShelterCheckin /></Suspense>} />
              <Route path="forms" element={<Suspense fallback={<PageLoader />}><FieldForms /></Suspense>} />
            </Route>

          {/* Admin routes (separate layout) */}
          <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminLayout /></Suspense>}>
            <Route index element={<AdminDashboard />} />
            <Route path="health" element={<AdminHealth />} />
            <Route path="maui-rollout" element={<AdminMauiRollout />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="partnerships" element={<AdminPartnerships />} />
            <Route path="shares" element={<AdminShares />} />
            <Route path="interfaces" element={<AdminInterfaces />} />
            <Route path="feature-flags" element={<AdminFeatureFlags />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="api-keys" element={<AdminApiKeys />} />
            <Route path="login-activity" element={<AdminLoginActivity />} />
            <Route path="error-reports" element={<AdminErrorReports />} />
            <Route path="support-tickets" element={<AdminSupportTickets />} />
            <Route path="support-tickets/:id" element={<AdminSupportTicketDetail />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
            <Route path="feature-flag-matrix" element={<AdminFeatureFlagMatrix />} />
            <Route path="onboarding" element={<TenantOnboardingWizard />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          </Route>{/* end OnboardingGuard */}
      </Route>{/* end RequireAuth */}
    </Route>
  )
);
